import React, { useState, useEffect, useRef, useCallback } from 'react';

const HealthDot = ({ status, latencyMs }) => {
  const colors = {
    online:  { bg: '#34d399', label: 'Online' },
    offline: { bg: '#f87171', label: 'Offline' },
    error:   { bg: '#fbbf24', label: 'Error' },
    unknown: { bg: '#64748b', label: 'Unknown' },
  };
  const c = colors[status] || colors.unknown;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ position: 'relative', width: '10px', height: '10px' }}>
        {status === 'online' && (
          <div style={{ position: 'absolute', inset: '-4px', borderRadius: '50%', background: c.bg + '44', animation: 'pulse 2s infinite' }} />
        )}
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: c.bg, position: 'relative' }} />
      </div>
      <span style={{ fontSize: '11px', color: c.bg, fontWeight: '600' }}>{c.label}</span>
      {latencyMs && status === 'online' && <span style={{ fontSize: '10px', color: '#64748b' }}>{latencyMs}ms</span>}
    </div>
  );
};

const EditTenantModal = ({ tenant, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: tenant.name || '', url: tenant.url || '', tailscaleIP: tenant.tailscaleIP || '', status: tenant.status || 'active' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await fetch('/api/tenants/' + tenant._id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('accessToken') },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (data.success) { onSaved(data.tenant); onClose(); }
      else setError(data.error || 'Save failed');
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#1e293b', borderRadius: '16px', padding: '28px', border: '1px solid #334155', width: '480px', maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h3 style={{ color: '#e2e8f0', margin: 0 }}>Edit Tenant</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '20px' }}>x</button>
        </div>
        {[
          { label: 'COMPANY NAME', key: 'name', placeholder: 'Acme Inc.' },
          { label: 'APP URL', key: 'url', placeholder: 'https://instance.tail190f6e.ts.net' },
          { label: 'TAILSCALE IP', key: 'tailscaleIP', placeholder: '100.x.x.x' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: '16px' }}>
            <label style={{ color: '#64748b', fontSize: '11px', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>{f.label}</label>
            <input value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '14px', boxSizing: 'border-box' }} />
          </div>
        ))}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ color: '#64748b', fontSize: '11px', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>STATUS</label>
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
            style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
        {error && <div style={{ color: '#f87171', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ background: '#7c3aed', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProfileGenerator = ({ tenant, onClose }) => {
  const [answers, setAnswers] = useState({ description: '', services: '', targetMarket: '', operationalArea: '', companyValues: '', teamSize: '', yearsInBusiness: '', aiTone: 'professional' });
  const [generating, setGenerating] = useState(false);
  const [profile, setProfile] = useState(tenant.companyProfile || null);
  const [error, setError] = useState('');

  const generate = async () => {
    setGenerating(true); setError('');
    try {
      const r = await fetch('/api/tenants/' + tenant._id + '/generate-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('accessToken') },
        body: JSON.stringify({ interviewAnswers: answers }),
      });
      const data = await r.json();
      if (data.success) setProfile(data.companyProfile);
      else setError(data.error || 'Generation failed');
    } catch (e) { setError(e.message); }
    setGenerating(false);
  };

  const questions = [
    { key: 'description', label: 'What does this company do?', placeholder: 'Security guard services...' },
    { key: 'services', label: 'Main services (comma separated)', placeholder: 'Armed security, patrol...' },
    { key: 'targetMarket', label: 'Who do they serve?', placeholder: 'Commercial property owners...' },
    { key: 'operationalArea', label: 'Where do they operate?', placeholder: 'Greater Seattle area...' },
    { key: 'companyValues', label: 'Company values (comma separated)', placeholder: 'Integrity, reliability...' },
    { key: 'teamSize', label: 'Approximate team size', placeholder: '25' },
    { key: 'yearsInBusiness', label: 'Years in business', placeholder: '5' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#1e293b', borderRadius: '16px', padding: '28px', border: '1px solid #334155', width: '600px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h3 style={{ color: '#e2e8f0', margin: 0 }}>AI Company Profile</h3>
            <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0' }}>{tenant.name}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '20px' }}>x</button>
        </div>
        {profile ? (
          <div>
            <div style={{ background: '#064e3b', border: '1px solid #34d399', borderRadius: '8px', padding: '12px', marginBottom: '20px', color: '#34d399', fontSize: '13px' }}>Profile generated successfully</div>
            <pre style={{ background: '#0f172a', padding: '16px', borderRadius: '8px', color: '#94a3b8', fontSize: '12px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{JSON.stringify(profile, null, 2)}</pre>
            <button onClick={() => setProfile(null)} style={{ marginTop: '16px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer' }}>Regenerate</button>
          </div>
        ) : (
          <>
            {questions.map(q => (
              <div key={q.key} style={{ marginBottom: '14px' }}>
                <label style={{ color: '#64748b', fontSize: '11px', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>{q.label.toUpperCase()}</label>
                <input value={answers[q.key]} onChange={e => setAnswers({ ...answers, [q.key]: e.target.value })} placeholder={q.placeholder}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#64748b', fontSize: '11px', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>AI TONE</label>
              <select value={answers.aiTone} onChange={e => setAnswers({ ...answers, aiTone: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="technical">Technical</option>
                <option value="formal">Formal</option>
              </select>
            </div>
            {error && <div style={{ color: '#f87171', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={generate} disabled={generating} style={{ background: generating ? '#4c1d95' : '#7c3aed', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                {generating ? 'Generating...' : 'Generate Profile'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const InviteForm = ({ onClose }) => {
  const [form, setForm] = useState({ companyName: '', adminEmail: '', industry: '', estimatedSize: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const industries = ['Security', 'Healthcare', 'Legal', 'Finance', 'Retail', 'Construction', 'Technology', 'Other'];
  const sizes = ['1-10', '11-50', '51-200', '201-500', '500+'];

  const submit = async () => {
    if (!form.companyName || !form.adminEmail) return;
    setLoading(true);
    try {
      const r = await fetch('/api/onboarding/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('accessToken') },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (data.success) setResult(data);
      else alert(data.error || JSON.stringify(data));
    } catch { alert('Failed to create invite'); }
    setLoading(false);
  };

  if (result) return (
    <div style={{ background: '#1e293b', borderRadius: '12px', padding: '24px', border: '1px solid #334155' }}>
      <div style={{ color: '#34d399', fontSize: '20px', marginBottom: '16px' }}>Invite Created!</div>
      <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '14px' }}>Send this link to <strong style={{ color: '#e2e8f0' }}>{form.adminEmail}</strong>:</p>
      <div style={{ background: '#0f172a', padding: '12px', borderRadius: '8px', marginBottom: '16px', wordBreak: 'break-all', color: '#7c3aed', fontSize: '13px' }}>{result.onboardingUrl}</div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={() => navigator.clipboard.writeText(result.onboardingUrl)} style={{ background: '#7c3aed', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>Copy Link</button>
        <button onClick={onClose} style={{ background: '#1e293b', color: '#94a3b8', padding: '8px 16px', borderRadius: '8px', border: '1px solid #334155', cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );

  return (
    <div style={{ background: '#1e293b', borderRadius: '12px', padding: '24px', border: '1px solid #334155', marginBottom: '20px' }}>
      <h3 style={{ color: '#e2e8f0', marginBottom: '20px' }}>Invite New Company</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        {[{ key: 'companyName', label: 'COMPANY NAME *', placeholder: 'Acme Security Inc.', type: 'text' },
          { key: 'adminEmail', label: 'ADMIN EMAIL *', placeholder: 'admin@acme.com', type: 'email' }].map(f => (
          <div key={f.key}>
            <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>{f.label}</label>
            <input value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} type={f.type}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '14px', boxSizing: 'border-box' }} />
          </div>
        ))}
        <div>
          <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>INDUSTRY</label>
          <select value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })}
            style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}>
            <option value="">Select...</option>
            {industries.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div>
          <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>SIZE</label>
          <select value={form.estimatedSize} onChange={e => setForm({ ...form, estimatedSize: e.target.value })}
            style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}>
            <option value="">Select...</option>
            {sizes.map(s => <option key={s} value={s}>{s} employees</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={submit} disabled={loading} style={{ background: '#7c3aed', color: 'white', padding: '10px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
          {loading ? 'Creating...' : 'Create Invite'}
        </button>
        <button onClick={onClose} style={{ background: 'transparent', color: '#94a3b8', padding: '10px 16px', borderRadius: '8px', border: '1px solid #334155', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
      </div>
    </div>
  );
};

const SSOButton = ({ tenant }) => {
  const [launching, setLaunching] = useState(false);
  const launch = async () => {
    setLaunching(true);
    try {
      const r = await fetch('/api/chairman/sso-launch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('accessToken') },
        body: JSON.stringify({ tenantUrl: tenant.url, email: 'tmarkham@mipcorporate.com', businessId: tenant.businessId }),
      });
      const data = await r.json();
      if (data.success) window.open(data.loginUrl, '_blank');
      else alert('SSO failed: ' + data.error);
    } catch (e) { alert('SSO error: ' + e.message); }
    setLaunching(false);
  };
  return (
    <button onClick={launch} disabled={launching} style={{ background: launching ? '#4c1d95' : '#7c3aed', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
      {launching ? 'Launching...' : 'Open Instance'}
    </button>
  );
};

const TenantDetail = ({ tenant: initialTenant, health, onBack, onUpdated }) => {
  const [tenant, setTenant] = useState(initialTenant);
  const [showEdit, setShowEdit] = useState(false);

  const tid = tenant._id ? (tenant._id.$oid || tenant._id.toString()) : ''; const h = health[tid] || {};

  return (
    <div style={{ padding: '20px' }}>
      <style>{`@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.8)} }`}</style>
      {showEdit && <EditTenantModal tenant={tenant} onClose={() => setShowEdit(false)} onSaved={updated => { setTenant(updated); if (onUpdated) onUpdated(updated); }} />}

      <button onClick={onBack} style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', marginBottom: '20px' }}>
        Back to Registry
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h2 style={{ color: '#e2e8f0', margin: 0 }}>{tenant.name}</h2>
          <p style={{ color: '#64748b', margin: '4px 0', fontSize: '13px' }}>{tenant.businessId}</p>
          <div style={{ marginTop: '8px' }}><HealthDot status={h.status || 'unknown'} latencyMs={h.latencyMs} /></div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', background: tenant.status === 'active' ? '#064e3b' : '#4b1113', color: tenant.status === 'active' ? '#34d399' : '#f87171' }}>{tenant.status}</span>
          <button onClick={() => setShowEdit(true)} style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'TAILSCALE IP', value: tenant.tailscaleIP || 'Not set' },
          { label: 'REGISTERED', value: tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : '-' },
          { label: 'LAST CHECKED', value: h.lastChecked ? new Date(h.lastChecked).toLocaleTimeString() : 'Never' },
          { label: 'LAST ONLINE', value: h.lastOnline ? new Date(h.lastOnline).toLocaleString() : 'Never' },
        ].map(card => (
          <div key={card.label} style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155' }}>
            <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '8px' }}>{card.label}</div>
            <div style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: 'bold' }}>{card.value}</div>
          </div>
        ))}
        <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155', gridColumn: '1 / -1' }}>
          <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '8px' }}>APP URL</div>
          {tenant.url ? <a href={tenant.url} target="_blank" rel="noreferrer" style={{ color: '#7c3aed' }}>{tenant.url}</a> : <span style={{ color: '#64748b' }}>Not configured</span>}
        </div>
      </div>
      {h.errorMessage && (
        <div style={{ background: '#4b1113', border: '1px solid #f87171', borderRadius: '8px', padding: '12px', marginBottom: '20px', color: '#f87171', fontSize: '13px' }}>
          Last error: {h.errorMessage}
        </div>
      )}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {tenant.url && <a href={tenant.url} target="_blank" rel="noreferrer" style={{ background: '#7c3aed', color: 'white', padding: '10px 20px', borderRadius: '8px', textDecoration: 'none', fontSize: '14px' }}>Open Instance</a>}

      </div>
    </div>
  );
};

const TenantRegistry = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [searchVal, setSearchVal] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [health, setHealth] = useState({});
  const sseRef = useRef(null);

  const loadTenants = useCallback(() => {
    fetch('/api/tenants', { headers: { Authorization: 'Bearer ' + localStorage.getItem('accessToken') } })
      .then(r => r.json())
      .then(data => { setTenants(data.tenants || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTenants();
    const token = localStorage.getItem('accessToken');
    // Fetch health immediately on mount
    fetch('/api/tenants/health', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json()).then(d => { if (d.health) setHealth(d.health); }).catch(() => {});
    // Then keep it live via SSE
    const sse = new EventSource('/api/tenants/health/stream');
    sseRef.current = sse;
    sse.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'health_update') setHealth(msg.data);
      } catch {}
    };
    return () => sse.close();
  }, [loadTenants]);

  const filtered = tenants.filter(t =>
    !searchVal || (t.name && t.name.toLowerCase().includes(searchVal.toLowerCase())) || (t.businessId && t.businessId.toLowerCase().includes(searchVal.toLowerCase()))
  );

  if (loading) return <div style={{ padding: '20px', color: '#94a3b8' }}>Loading tenants...</div>;
  if (selected) return (
    <TenantDetail tenant={selected} health={health} onBack={() => setSelected(null)}
      onUpdated={updated => { setTenants(prev => prev.map(t => t._id === updated._id ? updated : t)); setSelected(updated); }} />
  );

  const onlineCount = Object.values(health).filter(h => h.status === 'online').length;

  return (
    <div style={{ padding: '20px' }}>
      <style>{`@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.8)} }`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#7c3aed', margin: 0 }}>Tenant Registry</h2>
          <p style={{ color: '#64748b', margin: '4px 0', fontSize: '13px' }}>
            {tenants.length} tenants
            {Object.keys(health).length > 0 && <span style={{ color: '#34d399', marginLeft: '8px' }}>{onlineCount} online</span>}
          </p>
        </div>
        <button onClick={() => setShowInvite(true)} style={{ background: '#7c3aed', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
          Add Tenant
        </button>
      </div>
      {showInvite && <InviteForm onClose={() => { setShowInvite(false); loadTenants(); }} />}
      <input placeholder="Search tenants..." value={searchVal} onChange={e => setSearchVal(e.target.value)}
        style={{ width: '100%', padding: '10px 16px', borderRadius: '8px', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box' }} />
      {filtered.map(t => {
        const h = health[t._id ? (t._id.$oid || t._id.toString()) : ''] || {};
        return (
          <div key={t._id} onClick={() => setSelected(t)}
            style={{ background: '#1e293b', borderRadius: '12px', padding: '16px', marginBottom: '12px', border: '1px solid #334155', cursor: 'pointer', transition: 'border-color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#7c3aed'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: '#e2e8f0', margin: 0 }}>{t.name}</h3>
                <p style={{ color: '#64748b', margin: '4px 0', fontSize: '12px' }}>{t.url || 'No URL configured'}</p>
                <div style={{ marginTop: '6px' }}><HealthDot status={h.status || 'unknown'} latencyMs={h.latencyMs} /></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', background: t.status === 'active' ? '#064e3b' : '#4b1113', color: t.status === 'active' ? '#34d399' : '#f87171' }}>{t.status}</span>
                <span style={{ color: '#64748b', fontSize: '18px' }}>›</span>
              </div>
            </div>
          </div>
        );
      })}
      {filtered.length === 0 && <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>No tenants found</div>}
    </div>
  );
};

export default TenantRegistry;
